import {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {LuHistory, LuRefreshCw, LuRocket} from 'react-icons/lu';
import PublishDialog from '../../../../components/publish-dialog/publish-dialog';
import Message from '../../../../components/toast-message';
import {useAppInfo} from '../../../../helpers/hooks/common-hook';
import PageContainer from "../../../../components/page-container";
import {getAppVersions, getReviewRequest, setCurrentVersion} from '../../../../services/app/app-service';

dayjs.extend(relativeTime);

const exactTime = date => (date ? dayjs(date).format('MMM D, YYYY · h:mm A') : '');
const relative = date => (date ? dayjs(date).fromNow() : '');

// Publishing and "make live" both touch modifiedAt, so the newest is live.
const byModifiedDesc = (a, b) => new Date(b.modifiedAt || 0) - new Date(a.modifiedAt || 0);

function Time({ date, prefix }) {
    if (!date) return null;
    return <time dateTime={date} title={exactTime(date)}>{prefix}{relative(date)}</time>;
}

function VersionSkeleton() {
    return (
        <div aria-hidden="true" className="space-y-3">
            <div className="h-32 animate-pulse rounded-2xl bg-gray-100" />
            {[1, 2, 3].map(key => <div key={key} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
        </div>
    );
}

function VersionListPage() {
    const { appId } = useAppInfo();
    const location = useLocation();
    const publishButton = useRef(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewRequest, setReviewRequest] = useState(null);
    const [publishOpen, setPublishOpen] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);
    const [switchingId, setSwitchingId] = useState(null);
    const [toastState, setToastState] = useState({ message: '', type: '' });

    const fetchVersions = async () => {
        if (!appId) return;
        setLoading(true);
        const [{ versions: list } = {}, review] = await Promise.all([getAppVersions(appId), getReviewRequest(appId)]);
        setVersions(Array.isArray(list) ? [...list].sort(byModifiedDesc) : []);
        setReviewRequest(review || null);
        setLoading(false);
    };

    useEffect(() => {
        fetchVersions();
    }, [appId]);

    // Arriving from the sidebar's Publish focuses the Publish button.
    useEffect(() => {
        if (location.state?.focusPublish) publishButton.current?.focus();
    }, [location.key]);

    const handlePublishComplete = (success, message) => {
        setPublishOpen(false);
        if (success) {
            setToastState({ message, type: 'success' });
            fetchVersions();
        } else if (message) {
            setToastState({ message, type: 'failure' });
        }
    };

    const handleMakeLive = async version => {
        setConfirmingId(null);
        setSwitchingId(version.versionId);
        const response = await setCurrentVersion(appId, version);
        setSwitchingId(null);
        // The service returns [] when the request fails.
        if (Array.isArray(response)) {
            setToastState({ message: `Unable to make ${version.versionName || 'this version'} live`, type: 'failure' });
        } else {
            setToastState({ message: `${version.versionName || 'Version'} is now live`, type: 'success' });
        }
        fetchVersions();
    };

    const [live, ...history] = versions;

    return (
        <PageContainer
            title="Versions"
            headerAction={<div className="flex flex-row items-center gap-2">
                <button type="button" onClick={fetchVersions} disabled={loading} aria-label="Reload versions" title="Reload"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                    <LuRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                </button>
                <button ref={publishButton} type="button" onClick={() => setPublishOpen(true)} disabled={!appId}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-secondary hover:shadow-md focus:outline-none focus:ring-4 focus:ring-primary/30 focus:ring-offset-1 disabled:opacity-50">
                    <LuRocket className="h-4 w-4" aria-hidden="true" />
                    Publish
                    {reviewRequest && <span className="ml-1 h-2 w-2 rounded-full bg-red-400" title="Review request pending" />}
                </button>
            </div>}>

            {toastState.message &&
                <Message text={toastState.message} type={toastState.type} />
            }

            {publishOpen &&
                <PublishDialog appId={appId} onCompletion={handlePublishComplete} reviewRequest={reviewRequest} />
            }

            <div className="mx-auto w-full max-w-4xl pb-10 pt-2">
                <p className="mb-6 text-sm text-gray-500">
                    Publish your latest changes as a new version, or switch which version your channels use.
                </p>

                {reviewRequest && (
                    <section aria-label="Pending review" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                                Review requested{reviewRequest.versionRequest?.versionName ? `: ${reviewRequest.versionRequest.versionName}` : ''}
                            </p>
                            {reviewRequest.versionRequest?.releaseNote &&
                                <p className="mt-1 text-xs leading-5 text-amber-800/80 dark:text-amber-200/70">{reviewRequest.versionRequest.releaseNote}</p>}
                        </div>
                        <button type="button" onClick={() => setPublishOpen(true)}
                            className="shrink-0 rounded-lg bg-amber-900 px-3 py-1.5 text-xs font-medium text-fixed-white transition hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-900/30">
                            Review &amp; publish
                        </button>
                    </section>
                )}

                {loading && versions.length === 0 ? <VersionSkeleton /> : !live ? (
                    <section className="flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-500">
                            <LuRocket className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <h2 className="mt-4 text-sm font-semibold text-gray-900">No versions published yet</h2>
                        <p className="mt-1 max-w-sm text-sm text-gray-500">Publish your changes to create the first version your channels can use.</p>
                        <button type="button" onClick={() => setPublishOpen(true)}
                            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-secondary focus:outline-none focus:ring-4 focus:ring-primary/30">
                            <LuRocket className="h-4 w-4" aria-hidden="true" /> Publish first version
                        </button>
                    </section>
                ) : (
                    <>
                        <section aria-label="Live version" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> Live
                                </span>
                                <span className="text-xs text-gray-400">Used by your channels</span>
                            </div>
                            <h2 className="mt-3 break-words text-lg font-semibold tracking-tight text-gray-900">{live.versionName || 'Untitled version'}</h2>
                            {live.releaseNote && <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">{live.releaseNote}</p>}
                            <p className="mt-4 text-xs text-gray-500">
                                <Time date={live.createdAt} prefix="Published " />
                                {live.createdAt && live.modifiedAt && live.modifiedAt !== live.createdAt && <> · <Time date={live.modifiedAt} prefix="live since " /></>}
                                {!live.createdAt && <Time date={live.modifiedAt} prefix="Live since " />}
                            </p>
                        </section>

                        <section aria-labelledby="version-history" className="mt-8">
                            <h2 id="version-history" className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                                <LuHistory className="h-4 w-4 text-gray-400" aria-hidden="true" /> Earlier versions
                                <span className="font-normal text-gray-400">{history.length}</span>
                            </h2>
                            {history.length === 0 ? (
                                <p className="rounded-xl border border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                                    Earlier versions appear here after you publish again.
                                </p>
                            ) : (
                                <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white">
                                    {history.map(version => {
                                        const confirming = confirmingId === version.versionId;
                                        const switching = switchingId === version.versionId;
                                        return (
                                            <li key={version.versionId || version.versionName} className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:px-5">
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-medium text-gray-900">{version.versionName || 'Untitled version'}</p>
                                                    <p className="mt-0.5 truncate text-xs text-gray-500">
                                                        <Time date={version.createdAt || version.modifiedAt} />
                                                        {version.releaseNote && <> · {version.releaseNote}</>}
                                                    </p>
                                                </div>
                                                {confirming ? (
                                                    <div className="flex items-center gap-2" role="group" aria-label={`Confirm making ${version.versionName || 'this version'} live`}>
                                                        <span className="text-xs text-gray-500">Switch channels to this version?</span>
                                                        <button type="button" onClick={() => setConfirmingId(null)}
                                                            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">Cancel</button>
                                                        <button type="button" onClick={() => handleMakeLive(version)}
                                                            className="rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-white hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">Make live</button>
                                                    </div>
                                                ) : (
                                                    <button type="button" onClick={() => setConfirmingId(version.versionId)} disabled={Boolean(switchingId)}
                                                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
                                                        {switching ? 'Switching…' : 'Make live'}
                                                    </button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </section>
                    </>
                )}
            </div>
        </PageContainer>)
}

export default VersionListPage;
